import React from 'react';
import { META_LABEL } from '../playbooks/editorBits';

// Read-only structured preview of the definition (doc-74) — replaces the
// Fields repeater's readability without its two-way-sync complexity. Renders
// live from the last-valid parsed draft; the parent dims it while the JSON is
// unparseable. Defensive by construction: the definition may be mid-edit and
// carry meta-schema errors, so every access tolerates garbage.

const TYPE_LABELS = {
  string: 'String',
  number: 'Number',
  date: 'Date',
  boolean: 'Yes / No',
  enum: 'Choice',
  array: 'List',
  object: 'Object',
};

const isContainer = (type) => type === 'array' || type === 'object';

const typeChip = (type) => (
  <span className="chip sq gray">{TYPE_LABELS[type] || String(type || 'string')}</span>
);

function ItemFieldsTable({ itemFields }) {
  return (
    <table className="tbl sd-array-tbl" style={{ fontSize: 12 }}>
      <thead>
        <tr>
          <th style={{ width: '26%' }}>Column</th>
          <th style={{ width: '14%' }}>Type</th>
          <th>Instruction</th>
        </tr>
      </thead>
      <tbody>
        {itemFields.map((sf, j) => {
          const safe = sf && typeof sf === 'object' ? sf : {};
          return (
            <tr key={j}>
              <td>
                <div style={{ fontWeight: 500 }}>{String(safe.label || safe.key || `#${j + 1}`)}</div>
                {safe.key ? <div className="mono muted" style={{ fontSize: 11 }}>{String(safe.key)}</div> : null}
              </td>
              <td>{typeChip(safe.type || 'string')}</td>
              <td className="muted" style={{ fontSize: 11.5, lineHeight: 1.45 }}>
                {String(safe.description || '') || '—'}
                {safe.type === 'enum' && Array.isArray(safe.options) && safe.options.length > 0 && (
                  <div className="mono" style={{ fontSize: 11, marginTop: 2 }}>
                    {safe.options.map((o) => String(o)).join(' · ')}
                  </div>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function PreviewField({ field, index }) {
  const safe = field && typeof field === 'object' ? field : {};
  const itemFields = Array.isArray(safe.itemFields) ? safe.itemFields : [];
  return (
    <div className="pb-item">
      <div className="row" style={{ gap: 8, alignItems: 'baseline', flexWrap: 'wrap' }}>
        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)' }}>
          {String(safe.label || safe.key || `Field ${index + 1}`)}
        </span>
        {safe.key ? <span className="mono muted" style={{ fontSize: 12 }}>{String(safe.key)}</span> : null}
        {typeChip(safe.type || 'string')}
      </div>
      {safe.description ? (
        <div
          className="muted"
          title={String(safe.description)}
          style={{
            fontSize: 12,
            lineHeight: 1.5,
            marginTop: 4,
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          {String(safe.description)}
        </div>
      ) : null}
      {safe.type === 'enum' && Array.isArray(safe.options) && safe.options.length > 0 && (
        <div className="mono muted" style={{ fontSize: 11.5, marginTop: 4 }}>
          {safe.options.map((o) => String(o)).join(' · ')}
        </div>
      )}
      {isContainer(safe.type) && (
        <div style={{ marginTop: 8 }}>
          <div style={{ ...META_LABEL, marginBottom: 4 }}>
            {safe.type === 'array' ? 'Item fields — the columns of each list item' : "Item fields — the object's keys"}
          </div>
          {itemFields.length > 0 ? (
            /* Scroll region (doc-88): the table is intrinsically wide —
               scroll it sideways rather than wrap-crush the instruction
               column in a narrow rail. tabIndex makes it keyboard-scrollable. */
            <div className="sde-tbl-scroll" tabIndex={0} aria-label="Item fields — scrolls horizontally">
              <ItemFieldsTable itemFields={itemFields} />
            </div>
          ) : (
            <div className="muted" style={{ fontSize: 12 }}>
              None declared — the description alone guides extraction.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function SchemaPreview({ definition, dimmed }) {
  const fields = Array.isArray(definition?.fields) ? definition.fields : [];
  return (
    <div style={dimmed ? { opacity: 0.6 } : undefined}>
      {dimmed && (
        <div className="muted" style={{ fontSize: 12, marginBottom: 8 }}>
          Showing the last valid definition — fix the JSON to update.
        </div>
      )}
      {fields.length === 0 ? (
        <div className="muted" style={{ fontSize: 12 }}>
          No fields yet — the preview fills in as the definition does.
        </div>
      ) : (
        <div className="stack-sm">
          {fields.map((f, i) => <PreviewField key={i} field={f} index={i} />)}
        </div>
      )}
    </div>
  );
}
